/**
 * Weather Exception Tracker - Caching & Persistence Utilities
 * 
 * Handles:
 * - In-memory + localStorage caching for API responses (30-min TTL)
 * - User settings persistence as versioned JSON document
 * - Config export/import functionality
 * 
 * Cache key format: `wxexc_${userId}_${locationId}_${startDate}_${endDate}_${unitGroup}_${granularity}`
 * 
 * Storage Schema (localStorage key: `wxExceptionTracker_${userId}`):
 * {
 *   schemaVersion: 1,
 *   userId: string,
 *   locations: Array<{location, name}>,
 *   activeRuleset: Object,
 *   savedRulesets: Array<Object>,
 *   dateRangeState: { startPeriod, endPeriod },
 *   unitGroup: 'us' | 'metric',
 *   granularity: 'days' | 'days+hours',
 *   filter: 'all' | 'exceptions' | 'errors',
 *   pageSize: number,
 *   lastUpdated: number
 * }
 */

const SCHEMA_VERSION = 1;
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const MAX_CACHE_ENTRIES = 20; // Maximum number of cache entries to keep in localStorage

// In-memory cache store
const memoryCache = new Map();

/**
 * Generates a cache key for weather data
 * @param {string} userId - User ID from session
 * @param {string} location - Location identifier
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @param {string} unitGroup - 'us' or 'metric'
 * @param {string} granularity - 'days' or 'days+hours'
 * @returns {string} Cache key
 */
export function generateCacheKey(userId, location, startDate, endDate, unitGroup, granularity) {
  // Normalize location for consistent caching
  const normalizedLocation = String(location).toLowerCase().trim();
  return `wxexc_${userId}_${normalizedLocation}_${startDate || 'default'}_${endDate || 'default'}_${unitGroup}_${granularity}`;
}

/**
 * Stores weather data in cache (memory + localStorage)
 * @param {string} key - Cache key
 * @param {Object} data - Weather data to cache
 */
export function setCacheEntry(key, data) {
  const entry = {
    data,
    timestamp: Date.now(),
    expires: Date.now() + CACHE_TTL_MS
  };

  // Store in memory
  memoryCache.set(key, entry);

  // Store in localStorage (with error handling)
  try {
    const storageKey = `cache_${key}`;
    // Clean up expired/excess entries before adding new one
    pruneLocalStorageCache();
    localStorage.setItem(storageKey, JSON.stringify(entry));
  } catch (e) {
    // localStorage might be full or disabled - try to clear old cache and retry
    if (e.name === 'QuotaExceededError') {
      try {
        clearExpiredCacheEntries();
        const storageKey = `cache_${key}`;
        localStorage.setItem(storageKey, JSON.stringify(entry));
      } catch (retryError) {
        console.warn('Failed to cache to localStorage after cleanup:', retryError.message);
      }
    } else {
      console.warn('Failed to cache to localStorage:', e.message);
    }
  }
}

/**
 * Clears expired cache entries from localStorage
 */
function clearExpiredCacheEntries() {
  const now = Date.now();
  const keysToRemove = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('cache_wxexc_')) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const entry = JSON.parse(stored);
          if (!entry.expires || entry.expires < now) {
            keysToRemove.push(key);
          }
        }
      } catch (e) {
        // Remove corrupted entries
        keysToRemove.push(key);
      }
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key));
  return keysToRemove.length;
}

/**
 * Prunes localStorage cache to stay under MAX_CACHE_ENTRIES
 * Removes oldest entries first
 */
function pruneLocalStorageCache() {
  const cacheEntries = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('cache_wxexc_')) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const entry = JSON.parse(stored);
          cacheEntries.push({ key, timestamp: entry.timestamp || 0 });
        }
      } catch (e) {
        // Remove corrupted entries
        localStorage.removeItem(key);
      }
    }
  }
  
  // If over limit, remove oldest entries
  if (cacheEntries.length >= MAX_CACHE_ENTRIES) {
    // Sort by timestamp (oldest first)
    cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
    // Remove oldest entries to get under limit
    const toRemove = cacheEntries.slice(0, cacheEntries.length - MAX_CACHE_ENTRIES + 1);
    toRemove.forEach(entry => localStorage.removeItem(entry.key));
  }
}

/**
 * Retrieves weather data from cache
 * @param {string} key - Cache key
 * @returns {Object|null} Cached data or null if expired/missing
 */
export function getCacheEntry(key) {
  const now = Date.now();
  const storageKey = `cache_${key}`;

  // Check memory cache first
  if (memoryCache.has(key)) {
    const entry = memoryCache.get(key);
    if (entry.expires > now) {
      return { ...entry.data, _cached: true, _cachedAt: entry.timestamp };
    }
    // Expired - remove from memory
    memoryCache.delete(key);
  }

  // Check localStorage
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const entry = JSON.parse(stored);
      if (entry.expires > now) {
        // Restore to memory cache
        memoryCache.set(key, entry);
        return { ...entry.data, _cached: true, _cachedAt: entry.timestamp };
      }
      // Expired - remove from localStorage
      localStorage.removeItem(storageKey);
    }
  } catch (e) {
    console.warn('Failed to read from localStorage cache:', e.message);
  }

  return null;
}

/**
 * Clears all weather exception cache entries
 */
export function clearAllCache() {
  // Clear memory cache
  memoryCache.clear();

  // Clear localStorage cache entries
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('cache_wxexc_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn('Failed to clear localStorage cache:', e.message);
  }
}

/**
 * Gets the storage key for user settings
 * @param {string} userId - User ID from session
 * @returns {string} localStorage key
 */
function getSettingsStorageKey(userId) {
  return `wxExceptionTracker_${userId || 'anonymous'}`;
}

/**
 * Creates a default settings object
 * @param {string} userId - User ID
 * @returns {Object} Default settings
 */
function createDefaultSettings(userId) {
  return {
    schemaVersion: SCHEMA_VERSION,
    userId: userId || null,
    locations: [],
    activeRuleset: null,
    savedRulesets: [],
    dateRangeState: {
      startPeriod: 'next10days',
      endPeriod: null
    },
    unitGroup: 'metric',
    granularity: 'days',
    filter: 'all',
    pageSize: 25,
    lastUpdated: Date.now()
  };
}

/**
 * Migrates settings from older schema versions
 * @param {Object} settings - Settings object to migrate
 * @returns {Object} Migrated settings
 */
function migrateSettings(settings) {
  if (!settings || !settings.schemaVersion) {
    return createDefaultSettings(settings?.userId);
  }

  // Future migrations would go here
  // if (settings.schemaVersion < 2) { ... }

  return settings;
}

/**
 * Loads user settings from localStorage
 * @param {string} userId - User ID from session
 * @returns {Object} User settings
 */
export function loadSettings(userId) {
  try {
    const key = getSettingsStorageKey(userId);
    const stored = localStorage.getItem(key);
    if (stored) {
      const settings = JSON.parse(stored);
      return migrateSettings(settings);
    }
  } catch (e) {
    console.warn('Failed to load settings:', e.message);
  }
  return createDefaultSettings(userId);
}

/**
 * Saves user settings to localStorage
 * @param {string} userId - User ID from session
 * @param {Object} settings - Settings to save
 */
export function saveSettings(userId, settings) {
  try {
    const key = getSettingsStorageKey(userId);
    const toSave = {
      ...settings,
      schemaVersion: SCHEMA_VERSION,
      userId,
      lastUpdated: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(toSave));
  } catch (e) {
    console.warn('Failed to save settings:', e.message);
  }
}

/**
 * Exports settings as a downloadable JSON file
 * @param {Object} settings - Settings to export
 * @param {string} filename - Filename for download
 */
export function exportSettings(settings, filename = 'weather-exception-config.json') {
  const exportData = {
    ...settings,
    exportedAt: new Date().toISOString(),
    exportVersion: SCHEMA_VERSION
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Validates imported settings
 * @param {Object} data - Imported data to validate
 * @returns {Object} Validation result { valid: boolean, errors: string[], data: Object }
 */
export function validateImportedSettings(data) {
  const errors = [];

  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Invalid data format'], data: null };
  }

  // Check schema version
  if (!data.schemaVersion || typeof data.schemaVersion !== 'number') {
    errors.push('Missing or invalid schema version');
  } else if (data.schemaVersion > SCHEMA_VERSION) {
    errors.push(`Schema version ${data.schemaVersion} is newer than supported (${SCHEMA_VERSION})`);
  }

  // Validate locations
  if (data.locations && !Array.isArray(data.locations)) {
    errors.push('Locations must be an array');
  }

  // Validate rulesets
  if (data.savedRulesets && !Array.isArray(data.savedRulesets)) {
    errors.push('Saved rulesets must be an array');
  }

  // Validate unitGroup
  if (data.unitGroup && !['us', 'metric'].includes(data.unitGroup)) {
    errors.push('Unit group must be "us" or "metric"');
  }

  // Validate granularity
  if (data.granularity && !['days', 'days+hours'].includes(data.granularity)) {
    errors.push('Granularity must be "days" or "days+hours"');
  }

  // Validate filter
  if (data.filter && !['all', 'exceptions', 'errors'].includes(data.filter)) {
    errors.push('Filter must be "all", "exceptions", or "errors"');
  }

  if (errors.length > 0) {
    return { valid: false, errors, data: null };
  }

  // Migrate if needed
  const migratedData = migrateSettings(data);
  return { valid: true, errors: [], data: migratedData };
}

/**
 * Imports settings from a JSON file
 * @param {File} file - File to import
 * @returns {Promise<Object>} Import result { success: boolean, settings?: Object, error?: string }
 */
export function importSettingsFromFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const validation = validateImportedSettings(data);
        
        if (validation.valid) {
          resolve({ success: true, settings: validation.data });
        } else {
          resolve({ success: false, error: validation.errors.join('; ') });
        }
      } catch (err) {
        resolve({ success: false, error: 'Failed to parse JSON: ' + err.message });
      }
    };

    reader.onerror = () => {
      resolve({ success: false, error: 'Failed to read file' });
    };

    reader.readAsText(file);
  });
}

/**
 * Clears all user data (settings and cache) for a user
 * @param {string} userId - User ID
 */
export function clearAllUserData(userId) {
  // Clear settings
  try {
    const key = getSettingsStorageKey(userId);
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('Failed to clear settings:', e.message);
  }

  // Clear cache entries for this user
  try {
    const prefix = `cache_wxexc_${userId}_`;
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn('Failed to clear user cache:', e.message);
  }

  // Clear memory cache
  for (const key of memoryCache.keys()) {
    if (key.includes(`_${userId}_`)) {
      memoryCache.delete(key);
    }
  }
}
